import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { api, ApiClientError } from '../../services/api';
import useAuth from '../../hooks/useAuth';
import { useChat } from '../../hooks/useChat';
import type { Contact, Message, User } from '../../types';
import { displayPhone } from '../../utils/chat-helpers';
import {
  conversationTypeLabel,
  getChatCapabilities,
  getEffectiveConversationType,
  isConversationTypeSupported,
  getUnsupportedReason,
} from '../../utils/conversation-capabilities';
import MessageBubble from './MessageBubble';
import FilePreviewBar, { createFileEntry, type FileEntry } from './FilePreviewBar';
import CameraCapture from './CameraCapture';
import ChatActionsMenu from './ChatActionsMenu';
import EmojiPicker from './EmojiPicker';
import Avatar from './Avatar';
import QuickReplyAutocomplete from './QuickReplyAutocomplete';
import AIAssistant from './AIAssistant';
import ContactMemoryPanel from './ContactMemoryPanel';

const CUSTOMER_TYPES = new Set<Contact['relationshipType']>([
  'client',
  'lead',
  'vip',
]);

const toDisplayRelationship = (type?: Contact['relationshipType']): string => {
  if (type && CUSTOMER_TYPES.has(type)) return 'client';
  return type ?? 'unknown';
};

const toStoredRelationship = (
  display: string,
  current?: Contact['relationshipType'],
): Contact['relationshipType'] => {
  if (display === 'client') {
    return current && CUSTOMER_TYPES.has(current) ? current : 'client';
  }
  return display as Contact['relationshipType'];
};

const RELATIONSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: 'unknown', label: 'Niet gesorteerd' },
  { value: 'family', label: 'Familie' },
  { value: 'friend', label: 'Vriend' },
  { value: 'client', label: 'Klant' },
];



const formatRecordingTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const ChatWindow = () => {
  const {
    activeChat,
    messages,
    loadingMessages,
    sendMessage,
    selectChat,
    updateChat,
    systemStatus,
    accounts,
    offline,
  } = useChat();
  const { user } = useAuth();
  const [agents, setAgents] = useState<User[]>([]);
  const [draft, setDraft] = useState('');
  const [messageQuery, setMessageQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now);
  const [pendingFiles, setPendingFiles] = useState<FileEntry[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [scheduleMinDate] = useState(() =>
    new Date(Date.now() + 60_000).toISOString().slice(0, 16),
  );
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactUpdating, setContactUpdating] = useState(false);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const messageEnd = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const list = await api.contacts();
        if (active) setContacts(list);
      } catch {
        // ignore; selector will show disabled state
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const activeContact = useMemo(() => {
    const identifier = activeChat?.phone ?? activeChat?.externalId;
    if (!identifier || !activeChat) return undefined;
    return contacts.find(
      (contact) => contact.accountId === activeChat.accountId && contact.phone === identifier,
    );
  }, [contacts, activeChat]);

  const filteredMessages = useMemo(() => {
    const query = messageQuery.trim().toLowerCase();
    if (!query) return messages;
    return messages.filter((message) => message.content.toLowerCase().includes(query));
  }, [messages, messageQuery]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);

  useEffect(() => {
    messageEnd.current?.scrollIntoView({ block: 'end' });
  }, [filteredMessages]);

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activeChat || user?.role !== 'admin') {
      return undefined;
    }
    const controller = new AbortController();
    const loadAgents = async () => {
      try {
        const result = await api.users(controller.signal);
        setAgents(result.users);
      } catch {
        setAgents([]);
      }
    };
    void loadAgents();
    return () => controller.abort();
  }, [activeChat, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (composerRef.current && !composerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target as Node)) {
        setShowAttachMenu(false);
      }
    };
    if (showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAttachMenu]);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  if (!activeChat) {
    return (
      <section className="chat-window empty-chat" aria-label="Chatvenster">
        <div className="empty-illustration" aria-hidden="true">
          AI
        </div>
        <h2>Selecteer een chat</h2>
        <p>Kies links een gesprek om berichten en instellingen te bekijken.</p>
      </section>
    );
  }

  // Resolve the connector status for the active account — NOT the legacy
  // global systemStatus.whatsapp which uses accountId "local_qr" and may
  // be stuck on "connecting" while the actual account connector is "ready".
  const activeAccount = accounts.find(
    (a) => a.isActive && a.provider === 'local_qr',
  ) ?? accounts.find((a) => a.isActive);
  const activeAccountState = activeAccount?.status.state ?? systemStatus?.whatsapp.state ?? 'disconnected';
  const connectorReady = activeAccountState === 'ready' || activeAccountState === 'authenticated';

  const conversationType = getEffectiveConversationType(activeChat);
  const chatCapabilities = getChatCapabilities(activeChat);
  const conversationTypeSupported = isConversationTypeSupported(activeAccount, conversationType);
  const typeNotSupportedReason = conversationTypeSupported
    ? undefined
    : getUnsupportedReason(activeAccount, conversationType);

  // Manual sends (agent/admin) are only blocked by:
  // - offline (no network)
  // - connector not ready (WhatsApp not connected)
  // - conversation type not supported by the active WhatsApp provider
  // - opted_out for direct contacts (contact explicitly revoked consent)
  // Manual sends are NOT blocked by: consent missing, 24h window, automode,
  // AI provider, or safety checks — those only apply to autonomous AI sends.
  const consentBlocked = conversationType === 'direct' && activeChat.consent === 'opted_out';
  const typeBlocked = !conversationTypeSupported || !chatCapabilities.canSend;
  const canSend = !offline && connectorReady && !consentBlocked && !typeBlocked;
  const sendHint = offline
    ? 'Je bent offline.'
    : !connectorReady
      ? activeAccount?.status.reason ?? systemStatus?.whatsapp.reason ?? 'WhatsApp is niet verbonden.'
      : typeBlocked
        ? (typeNotSupportedReason ?? 'Dit gesprekstype kan niet via de verbonden WhatsApp-provider worden verstuurd.')
        : consentBlocked
          ? 'Deze contact heeft toestemming ingetrokken (opted_out).'
          : '';
  const paused =
    chatCapabilities.canAutomode &&
    activeChat.pausedUntil !== undefined &&
    Date.parse(activeChat.pausedUntil) > currentTime;

  const triggerFileInput = (accept: string, capture?: 'environment' | 'user'): void => {
    // Create a fresh, temporarily-visible input. Some browsers ignore capture
    // on a hidden (display: none) input, so we position it off-screen instead.
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = !capture;
    if (capture) {
      input.setAttribute('capture', capture);
    }
    input.style.position = 'fixed';
    input.style.top = '-1000px';
    input.style.left = '-1000px';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    input.style.width = '1px';
    input.style.height = '1px';
    input.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (files && files.length > 0) {
        const entries = Array.from(files).map(createFileEntry);
        setPendingFiles((prev) => [...prev, ...entries]);
      }
      document.body.removeChild(input);
    };
    document.body.appendChild(input);
    input.click();
    setShowAttachMenu(false);
  };

  const removeFile = (id: string) => {
    setPendingFiles((prev) => {
      const removed = prev.find((entry) => entry.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((entry) => entry.id !== id);
    });
  };

  const b64ToFile = (b64: string, filename: string, mimeType: string): File => {
    const byteString = atob(b64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i += 1) {
      bytes[i] = byteString.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mimeType });
  };

  const handleGenerateImage = async () => {
    setShowAttachMenu(false);
    const prompt = window.prompt('Beschrijf de afbeelding die je wilt genereren:');
    if (!prompt || !prompt.trim()) return;
    setGeneratingImage(true);
    try {
      const { data, mimeType, filename } = await api.generateMedia({ prompt: prompt.trim() });
      const file = b64ToFile(data, filename, mimeType);
      setPendingFiles((prev) => [...prev, createFileEntry(file)]);
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Afbeelding genereren mislukt.';
      window.alert(message);
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setDraft((prev) => prev + emoji);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch {
      // Microphone access denied or unavailable
    }
  };

  const stopRecording = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        resolve(blob);
      };
      recorder.stop();
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecording(false);
    });
  };

  const cancelRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => {
        const stream = recorder.stream;
        stream.getTracks().forEach((track) => track.stop());
      };
      recorder.stop();
    }
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingTime(0);
  };

  const sendVoiceMessage = async () => {
    const blob = await stopRecording();
    if (!blob || !canSend) {
      setRecordingTime(0);
      return;
    }
    setSubmitting(true);
    try {
      const buffer = await blob.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
      await api.sendMedia({
        chatId: activeChat.id,
        clientMessageId: `voice:${Date.now()}`,
        mediaType: 'audio',
        mimeType: 'audio/webm',
        filename: `voice-${Date.now()}.webm`,
        data: base64,
      });
    } catch {
      // server error shown in banner
    } finally {
      setSubmitting(false);
      setRecordingTime(0);
    }
  };

  const handleQuickReplySelect = (reply: { content: string }) => {
    setDraft((current) => current.replace(/\/\w*$/, reply.content));
  };

  const handleSchedule = async () => {
    if (!activeChat || !draft.trim() || !scheduleAt) return;
    setScheduling(true);
    try {
      const iso = new Date(scheduleAt).toISOString();
      await api.createScheduledMessage({
        chatId: activeChat.id,
        content: draft.trim(),
        scheduledAt: iso,
      });
      setDraft('');
      setScheduleAt('');
      setShowSchedule(false);
    } catch {
      // error shown globally
    } finally {
      setScheduling(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content && pendingFiles.length === 0) return;
    if (!canSend) return;
    setSubmitting(true);
    try {
      if (pendingFiles.length > 0) {
        setUploadingFiles(true);
        for (const entry of pendingFiles) {
          const buffer = await entry.file.arrayBuffer();
          const base64 = btoa(
            String.fromCharCode(...new Uint8Array(buffer)),
          );
          await api.sendMedia({
            chatId: activeChat.id,
            clientMessageId: `media:${entry.id}`,
            mediaType: entry.type,
            mimeType: entry.file.type || 'application/octet-stream',
            filename: entry.file.name,
            data: base64,
            ...(content ? { caption: content } : {}),
          });
          URL.revokeObjectURL(entry.previewUrl);
        }
        setUploadingFiles(false);
        setPendingFiles([]);
      }
      if (content) {
        await sendMessage(content, replyTo?.id);
      }
      setDraft('');
      setReplyTo(null);
      setShowEmojiPicker(false);
    } catch {
      // The shared error banner exposes the safe server error.
    } finally {
      setSubmitting(false);
    }
  };

  const applyUpdate = async (update: Parameters<typeof updateChat>[0]) => {
    setUpdating(true);
    try {
      await updateChat(update);
    } catch {
      // The shared error banner exposes the safe server error.
    } finally {
      setUpdating(false);
    }
  };

  const handleRelationshipChange = async (relationshipType: Contact['relationshipType']) => {
    const identifier = activeChat?.phone ?? activeChat?.externalId;
    if (!identifier || !activeChat) return;
    setContactUpdating(true);
    try {
      if (activeContact) {
        const updated = await api.updateContact(activeContact.id, { relationshipType });
        setContacts((current) =>
          current.map((contact) => (contact.id === updated.id ? updated : contact)),
        );
      } else {
        const created = await api.createContact({
          accountId: activeChat.accountId,
          phone: identifier,
          name: activeChat.title,
          relationshipType,
        });
        setContacts((current) => [...current, created]);
      }
      window.dispatchEvent(new CustomEvent('wabrain-contacts-updated'));
    } catch {
      // The shared error banner exposes the safe server error.
    } finally {
      setContactUpdating(false);
    }
  };

  return (
    <section className="chat-window" aria-label={`Chat met ${activeChat.title}`}>
      <header className="chat-header">
        <div className="chat-header-main">
          <button
            type="button"
            className="button icon mobile-back"
            onClick={() => selectChat(null)}
            aria-label="Terug naar chatlijst"
          >
            ←
          </button>
          <Avatar chatId={activeChat.id} title={activeChat.title} size={40} className="header-avatar" />
          <div className="chat-heading">
            <h2>{activeChat.title}</h2>
            <span className="chat-status-line">
              {conversationType === 'direct'
                ? displayPhone(activeChat.phone, activeChat.externalId)
                : conversationTypeLabel(conversationType)}
              {connectorReady && <span className="status-pill online">verbonden</span>}
              {paused && <span className="status-pill paused">gepauzeerd</span>}
            </span>
          </div>
          <div className="chat-header-actions">
            {chatCapabilities.canCall && (
              <>
                <button
                  type="button"
                  className="icon-button call-audio"
                  disabled
                  aria-label="Audio-oproep niet beschikbaar"
                  title="Niet beschikbaar via de verbonden WhatsApp-provider"
                >
                  <span aria-hidden="true">📞</span>
                </button>
                <button
                  type="button"
                  className="icon-button call-video"
                  disabled
                  aria-label="Video-oproep niet beschikbaar"
                  title="Niet beschikbaar via de verbonden WhatsApp-provider"
                >
                  <span aria-hidden="true">📹</span>
                </button>
              </>
            )}
            <label className="header-search">
              <span className="sr-only">Berichten doorzoeken</span>
              <input
                type="search"
                aria-label="Berichten doorzoeken"
                value={messageQuery}
                onChange={(event) => setMessageQuery(event.target.value)}
                placeholder="🔍 Berichten doorzoeken"
              />
            </label>
            <ChatActionsMenu chat={activeChat} />
          </div>
        </div>
        <div className="chat-controls">
          {chatCapabilities.canChangeConsent && (
            <label>
              <span>Toestemming</span>
              <select
                aria-label="Toestemmingsstatus"
                value={activeChat.consent}
                disabled={updating}
                onChange={(event) =>
                  void applyUpdate({
                    consent: event.target.value as typeof activeChat.consent,
                  })
                }
              >
                <option value="unknown">Onbekend</option>
                <option value="opted_in">Ja</option>
                <option value="opted_out">Nee</option>
              </select>
            </label>
          )}
          {chatCapabilities.canAutomode && (
            <label>
              <span>Auto</span>
              <select
                aria-label="Automode voor deze chat"
                value={activeChat.automode}
                disabled={updating}
                onChange={(event) =>
                  void applyUpdate({
                    automode: event.target.value as typeof activeChat.automode,
                  })
                }
              >
                <option value="inherit">Volg</option>
                <option value="on">Aan</option>
                <option value="off">Uit</option>
              </select>
            </label>
          )}
          {chatCapabilities.canAutomode && (
            <button
              type="button"
              className="button secondary small"
              disabled={updating}
              onClick={() =>
                void applyUpdate({
                  pausedUntil: paused
                    ? null
                    : new Date(Date.now() + 60 * 60 * 1_000).toISOString(),
                })
              }
            >
              {paused ? '▶' : '⏸'}
            </button>
          )}
          {user?.role === 'admin' && chatCapabilities.canAssignAgent && (
            <label>
              <span>Agent</span>
              <select
                aria-label="Toegewezen agent"
                value={activeChat.ownerUserId}
                disabled={updating}
                onChange={(event) =>
                  void applyUpdate({
                    ownerUserId: event.target.value,
                  })
                }
              >
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.profile.displayName || agent.username}
                  </option>
                ))}
              </select>
            </label>
          )}
          {chatCapabilities.canChangeRelationship && (
            <label>
              <span>Soort</span>
              <select
                aria-label="Relatie met contact"
                value={toDisplayRelationship(activeContact?.relationshipType)}
                disabled={contactUpdating}
                onChange={(event) =>
                  void handleRelationshipChange(
                    toStoredRelationship(event.target.value, activeContact?.relationshipType),
                  )
                }
              >
                {RELATIONSHIP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </header>
      {chatCapabilities.canShowContactMemory && <ContactMemoryPanel activeChat={activeChat} />}
      {paused && (
        <div className="status-strip warning" role="status">
          Automode is voor deze chat gepauzeerd.
        </div>
      )}
      <div className="messages" aria-live="polite" aria-busy={loadingMessages}>
        {(() => {
          const pinned = filteredMessages.filter((m) => m.pinned).sort(
            (a, b) => Date.parse(b.pinnedAt ?? b.createdAt) - Date.parse(a.pinnedAt ?? a.createdAt),
          );
          if (pinned.length === 0) return null;
          const first = pinned[0]!;
          const rest = pinned.slice(1);
          return (
            <div className="pinned-messages-bar">
              <div className="pinned-message">
                <span className="pinned-icon">📌</span>
                <span className="pinned-content">
                  {first.content.slice(0, 80) || (first.media ? 'Media' : '')}
                </span>
              </div>
              {rest.length > 0 && (
                <button type="button" className="pinned-more" aria-label="Toon meer vastgezette berichten">
                  +{rest.length}
                </button>
              )}
            </div>
          );
        })()}
        {loadingMessages && filteredMessages.length === 0 && (
          <p className="empty-state">Berichten laden…</p>
        )}
        {!loadingMessages && filteredMessages.length === 0 && (
          <p className="empty-state">
            {messageQuery ? 'Geen berichten gevonden.' : 'Nog geen berichten in deze chat.'}
          </p>
        )}
        {filteredMessages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onReply={setReplyTo}
          />
        ))}
        <div ref={messageEnd} />
      </div>
      <form className="message-composer" onSubmit={handleSubmit}>
        {replyTo && (
          <div className="composer-reply-preview" role="status">
            <div className="composer-reply-content">
              <span className="composer-reply-sender">
                {replyTo.direction === 'outbound' ? 'Jij' : 'Contact'}
              </span>
              <span className="composer-reply-text">
                {replyTo.content.slice(0, 60) || (replyTo.media ? 'Media' : '')}
              </span>
            </div>
            <button
              type="button"
              className="composer-reply-close"
              aria-label="Annuleer beantwoorden"
              onClick={() => setReplyTo(null)}
            >
              ×
            </button>
          </div>
        )}
        {!canSend && (
          <p className="composer-hint" id="send-policy-hint">
            {sendHint}
          </p>
        )}
        {uploadingFiles && (
          <p className="composer-hint" role="status">
            Bestanden uploaden…
          </p>
        )}
        {generatingImage && (
          <p className="composer-hint" role="status">
            Afbeelding genereren…
          </p>
        )}
        <FilePreviewBar files={pendingFiles} onRemove={removeFile} />
        {isRecording ? (
          <div className="recording-controls">
            <div className="recording-indicator">
              <span className="recording-dot" />
              <span className="recording-timer">{formatRecordingTime(recordingTime)}</span>
            </div>
            <button
              type="button"
              className="recording-cancel"
              onClick={cancelRecording}
              aria-label="Opname annuleren"
            >
              ✕
            </button>
            <button
              type="button"
              className="recording-send"
              onClick={sendVoiceMessage}
              disabled={submitting || !canSend}
              aria-label="Voice-bericht versturen"
            >
              ➤
            </button>
          </div>
        ) : (
          <div className="composer-row" ref={composerRef} style={{ position: 'relative' }}>
            <QuickReplyAutocomplete draft={draft} onSelect={handleQuickReplySelect} />
            <div className="attach-menu-wrapper" ref={attachMenuRef}>
              <button
                type="button"
                className="button secondary icon attach-button"
                onClick={() => setShowAttachMenu((prev) => !prev)}
                disabled={submitting || offline || generatingImage}
                aria-label="Bijlage toevoegen"
                aria-expanded={showAttachMenu}
              >
                <span aria-hidden="true">{'\u2795'}</span>
              </button>
              {showAttachMenu && (
                <div className="attach-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => triggerFileInput('*/*')}
                  >
                    Document
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => triggerFileInput('image/*,video/*')}
                  >
                    Foto's en video's
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={generatingImage}
                    onClick={() => void handleGenerateImage()}
                  >
                    {generatingImage ? 'Afbeelding genereren…' : 'Genereer afbeelding'}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      // On mobile-like devices open the native camera capture;
                      // on desktop open an in-browser getUserMedia capture modal.
                      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                      if (isMobile) {
                        triggerFileInput('image/*', 'environment');
                      } else {
                        setShowAttachMenu(false);
                        setShowCameraCapture(true);
                      }
                    }}
                  >
                    Camera
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => triggerFileInput('audio/*')}
                  >
                    Audio
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      window.alert('Contact is nog niet beschikbaar.');
                      setShowAttachMenu(false);
                    }}
                  >
                    Contact
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      window.alert('Peiling is nog niet beschikbaar.');
                      setShowAttachMenu(false);
                    }}
                  >
                    Peiling
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      window.alert('Evenement is nog niet beschikbaar.');
                      setShowAttachMenu(false);
                    }}
                  >
                    Evenement
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      window.alert('Nieuwe sticker is nog niet beschikbaar.');
                      setShowAttachMenu(false);
                    }}
                  >
                    Nieuwe sticker
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      window.alert('Catalogus is nog niet beschikbaar.');
                      setShowAttachMenu(false);
                    }}
                  >
                    Catalogus
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      window.alert('Snelle antwoorden zijn nog niet beschikbaar.');
                      setShowAttachMenu(false);
                    }}
                  >
                    Snelle antwoorden
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              className={`emoji-toggle ${showEmojiPicker ? 'active' : ''}`}
              onClick={() => setShowEmojiPicker((prev) => !prev)}
              disabled={submitting || offline}
              aria-label="Emoji's openen"
            >
              {'\uD83D\uDE42'}
            </button>
            {showEmojiPicker && <EmojiPicker onSelect={handleEmojiSelect} />}
            {showCameraCapture && (
              <CameraCapture
                onCancel={() => setShowCameraCapture(false)}
                onCapture={(file) => {
                  setShowCameraCapture(false);
                  setPendingFiles((prev) => [...prev, createFileEntry(file)]);
                }}
              />
            )}
            <label className="sr-only" htmlFor="message-draft">
              Bericht
            </label>
            <textarea
              id="message-draft"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Schrijf een bericht… (typ / voor quick replies)"
              rows={1}
              maxLength={4096}
              disabled={submitting || offline}
              aria-describedby={!canSend ? 'send-policy-hint' : undefined}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
            />
            <button
              type="button"
              className="button secondary icon schedule-button"
              onClick={() => setShowSchedule((prev) => !prev)}
              disabled={submitting || offline || !draft.trim()}
              aria-label="Bericht inplannen"
              title="Bericht inplannen"
            >
              <span aria-hidden="true">{'\u23F0'}</span>
            </button>
            <button
              type="button"
              className="voice-record-button"
              onClick={startRecording}
              disabled={submitting || offline || !canSend}
              aria-label="Voice-bericht opnemen"
            >
              {'\uD83C\uDF99\uFE0F'}
            </button>
            <button
              type="submit"
              className="button primary send-button"
              disabled={!canSend || submitting || (!draft.trim() && pendingFiles.length === 0)}
            >
              <span className="sr-only">Bericht versturen</span>
              <span aria-hidden="true">➤</span>
            </button>
          </div>
        )}
        {showSchedule && !isRecording && (
          <div className="schedule-composer">
            <label className="tool-field">
              <span>Verzenden op</span>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(event) => setScheduleAt(event.target.value)}
                min={scheduleMinDate}
              />
            </label>
            <button
              type="button"
              className="button primary small"
              disabled={scheduling || !scheduleAt || !draft.trim()}
              onClick={handleSchedule}
            >
              {scheduling ? 'Inplannen…' : 'Inplannen'}
            </button>
            <button
              type="button"
              className="button secondary small"
              onClick={() => setShowSchedule(false)}
            >
              Annuleren
            </button>
          </div>
        )}
        {draft.length > 0 && (
          <p className="composer-counter muted small" aria-live="polite">
            {draft.length}/4096
          </p>
        )}
      </form>
      <AIAssistant
        activeChat={activeChat}
        messages={messages}
        onInsertDraft={(content) => setDraft(content)}
      />
    </section>
  );
};

export default ChatWindow;
